import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MailCheck, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { maskEmail } from "@/utils/email";

/**
 * Phase 8.1 — "Controleer je e-mail" gate.
 *
 * Rendered inside RequireAuth when the current user's `email_verified`
 * is false. Shows the masked destination address, offers resend, and
 * lets the user log out. The gate is server-enforced too; this screen
 * is the friendly companion.
 */
export default function EmailVerificationRequired() {
  const { t } = useI18n();
  const { user, logout, refreshUser } = useAuth();
  const [masked, setMasked] = useState(() => maskEmail(user?.email || ""));
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);

  // Fetch the authoritative masked email from the backend so we
  // never render an unmasked address by accident.
  useEffect(() => {
    let mounted = true;
    api.get("/auth/verification-status")
      .then(({ data }) => {
        if (!mounted) return;
        if (data?.email_masked) setMasked(data.email_masked);
        if (data?.email_verified) refreshUser();
      })
      .catch(() => { /* silent */ });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer when the server responded with a retry_after.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const resend = async () => {
    if (resending || cooldown > 0) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        toast.success(t("verify_email_status_verified"));
        try { await refreshUser(); } catch { /* ignore */ }
        return;
      }
      toast.success(t("verify_email_resend_success"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail?.retry_after) {
        setCooldown(Number(detail.retry_after) || 60);
        toast.error(t("verify_email_rate_limited"));
      } else {
        toast.error(t("error"));
      }
    } finally {
      setResending(false);
    }
  };

  const checkAgain = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await refreshUser();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="min-h-screen grid place-items-center px-4 py-10 bg-[hsl(var(--bg))]"
      data-testid="email-verification-required"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="card p-7 max-w-md w-full"
      >
        <div className="eyebrow mb-2">Rostr</div>
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] mb-3">
          <MailCheck size={20} strokeWidth={1.9} />
        </div>
        <h1
          className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))] mb-2"
          data-testid="verify-email-title"
        >
          {t("verify_email_title")}
        </h1>
        <p className="text-[14px] text-[hsl(var(--text-muted))] mb-3 leading-relaxed">
          {t("verify_email_check_body")}
        </p>
        <div
          className="rounded-xl bg-[hsl(var(--muted))] px-3 py-2 text-[14px] font-mono text-[hsl(var(--text-strong))] break-all mb-4"
          data-testid="verify-email-masked"
        >
          {masked || "—"}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={resend}
            disabled={resending || cooldown > 0}
            data-testid="verify-email-resend"
            className="btn btn-primary flex-1"
          >
            {resending ? (
              <Loader2 className="animate-spin" size={14} />
            ) : cooldown > 0 ? (
              `${t("verify_email_resend_wait")} (${cooldown}s)`
            ) : (
              t("verify_email_resend")
            )}
          </button>
          <button
            onClick={checkAgain}
            disabled={checking}
            data-testid="verify-email-refresh"
            className="btn btn-ghost flex-1"
          >
            {checking ? <Loader2 className="animate-spin" size={14} /> : t("verify_email_check_again")}
          </button>
        </div>
        <div className="border-t hairline mt-6 pt-4 flex items-center justify-between">
          <span className="text-[12px] text-[hsl(var(--text-soft))]">
            {t("verify_email_wrong_email_hint")}
          </span>
          <button
            onClick={logout}
            data-testid="verify-email-logout"
            className="text-[13px] font-semibold text-[hsl(var(--text))] hover:text-[hsl(var(--danger))] transition-colors inline-flex items-center gap-1.5"
          >
            <LogOut size={13} strokeWidth={2} />
            {t("verify_email_logout")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
